import { Box, Paper, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { numberHelper } from '../../utils/numberHelper';
import { ReactNode } from 'react';
import { iconMap } from '../../utils/helpers';

export interface MembershipLevelCardProps {
  selected: boolean;
  promotionalLabel: string;
  planLabel: MembershipLevel;
  priceAfterDiscount: number;
  baseCost: number;
  recurringPeriodLabel: string;
  showPriceAsFree: boolean;
  onClick: () => void;
  children?: ReactNode;
  isDesktop?: boolean;
}

export const MembershipLevelCard: (props: MembershipLevelCardProps) => JSX.Element = ({
  selected,
  promotionalLabel,
  planLabel,
  priceAfterDiscount,
  baseCost,
  recurringPeriodLabel,
  showPriceAsFree,
  onClick,
  children,
  isDesktop,
}) => {
  const mapMembershipTitle = (membershipLevel: MembershipLevel) => {
    switch (membershipLevel) {
      case MembershipLevel.CLASSIC:
        return 'Classic';
      case MembershipLevel.PLUS:
        return 'Plus';
      case MembershipLevel.PREMIER:
        return 'Premier';
      default:
        return '';
    }
  };

  return (
    <Paper
      elevation={8}
      className={`membership-level-card-${planLabel}`}
      sx={(theme) => ({
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        minWidth: '100px',
        minHeight: '150px',
        width: '100%',
        border: selected ? '2px solid #016BCD' : 'inherit',
        [theme.breakpoints.up('lg')]: {
          border: 'none',
        },
      })}
      onClick={onClick}
    >
      <Stack sx={{ height: '100%', display: 'contents' }}>
        <Box
          sx={(theme) => ({
            backgroundColor: selected ? '#ECF5FF' : 'inherit',
            [theme.breakpoints.up('lg')]: {
              backgroundColor:
                planLabel === MembershipLevel.CLASSIC
                  ? '#ECF5FF'
                  : planLabel === MembershipLevel.PLUS
                  ? '#FEF4F2'
                  : '#FAF5FF',
            },
          })}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            gap="0.5rem"
            sx={(theme) => ({
              height: '2.125rem',
              [theme.breakpoints.up('lg')]: {
                height: '2.75rem',
              },
            })}
          >
            {isDesktop && iconMap(planLabel)}
            <Typography
              variant="b2Medium"
              sx={(theme) => ({
                color: selected ? '#016BCD' : '#876647',
                [theme.breakpoints.up('lg')]: {
                  color:
                    planLabel === MembershipLevel.CLASSIC
                      ? '#016BCD'
                      : planLabel === MembershipLevel.PLUS
                      ? '#BE3317'
                      : '#7527B9',
                },
              })}
            >
              {promotionalLabel}
            </Typography>
          </Stack>
        </Box>
        <Stack
          sx={(theme) => ({
            padding: '10px',
            paddingRight: '0.25rem',
            paddingLeft: '0.25rem',
            paddingBottom: '1rem',
            justifyContent: 'space-between',
            textAlign: 'center',
            flex: '1',
            gap: '0.25rem',
            [theme.breakpoints.up('lg')]: {
              padding: '1rem',
            },
          })}
        >
          <Typography
            variant={{ xs: 'b1Regular', lg: 'h5Medium' }}
            sx={{
              lineHeight: '1.5rem',
              color: '#3D4043',
            }}
          >
            {mapMembershipTitle(planLabel)}

            <sup
              style={{
                verticalAlign: 'text-bottom',
                fontSize: 'x-small',
                opacity: planLabel === MembershipLevel.CLASSIC ? 0 : 1,
                visibility: planLabel === MembershipLevel.CLASSIC ? 'hidden' : 'visible',
              }}
            >
              ®
            </sup>
          </Typography>
          <Typography
            className="price-after-discount"
            sx={{ color: '#3D4043', alignSelf: 'center' }}
            variant={{ xs: 'h5', lg: 'h2' }}
          >
            {showPriceAsFree ? 'FREE' : numberHelper.formatMoney(priceAfterDiscount ?? 0)}
          </Typography>
          <Typography
            variant={{ xs: 'b3Regular', lg: 'b1Regular' }}
            sx={{ color: '#6C7175' }}
            className="member-level-price-section"
          >
            {priceAfterDiscount === baseCost ? (
              <span className="per-year-month-text" style={{ whiteSpace: 'pre' }}>
                {' '}
                {recurringPeriodLabel}
              </span>
            ) : (
              <span className="original-price-container">
                <span className="strikethrough">{numberHelper.formatMoney(baseCost ?? 0)}</span>
                <span className="per-year-month-text"> {recurringPeriodLabel}</span>
              </span>
            )}
          </Typography>
        </Stack>
        {isDesktop && <Box sx={{ padding: '0 1rem 1.5rem 1rem' }}>{children}</Box>}
      </Stack>
    </Paper>
  );
};

export default MembershipLevelCard;
